/**
 * Layout Injection
 *
 * Injects topbar and footer into pages
 */

import { api } from '../api/client.js';
import type { UserInfo } from '../api/types.js';
import { escapeHtml } from '../utils.js';
import { getHelpForCurrentPage } from '../ui/help.js';

// Start version fetch immediately at module load (runs in parallel with auth check)
const versionPromise = api.getVersion().catch(() => null);

/**
 * Generate topbar HTML
 */
function generateTopbar(user: UserInfo | null): string {
  if (!user) {
    return `
      <div id="topbar" class="topbar">
        <span class="topbar-title">VMSMan.IO</span>
        <span class="topbar-spacer"></span>
        <a href="/login">Login</a>
      </div>
    `;
  }

  const helpLink = getHelpForCurrentPage()
    ? `<a href="#" onclick="showHelpDialog(); return false;">Help</a>
      <span class="topbar-separator">|</span>`
    : '';

  return `
    <div id="topbar" class="topbar">
      <a href="/" class="topbar-title">VMSMan.IO</a>
      <span class="topbar-spacer"></span>
      <span class="topbar-user">
        Logged in as: <strong>${escapeHtml(user.username)}</strong>
      </span>
      <span class="topbar-separator">|</span>
      ${helpLink}
      <a href="#" onclick="userSettings(); return false;">Settings</a>
      <span class="topbar-separator">|</span>
      <a href="#" onclick="doLogout(); return false;">Logout</a>
    </div>
  `;
}

/**
 * Generate footer HTML
 */
function generateFooter(): string {
  return `
    <div id="footer" class="footer">
      <span id="version-info"></span>
    </div>
  `;
}

/**
 * Inject layout components into the page
 */
export function injectLayout(user: UserInfo | null): void {
  // Inject topbar
  const topbarContainer = document.getElementById('topbar-container');
  if (topbarContainer) {
    topbarContainer.innerHTML = generateTopbar(user);
  }

  // Inject footer
  const footerContainer = document.getElementById('footer-container');
  if (footerContainer) {
    footerContainer.innerHTML = generateFooter();
  }

  // Apply pre-fetched version to the now-injected footer element
  versionPromise.then((result) => {
    if (!result) return;
    const el = document.getElementById('version-info');
    if (el) el.textContent = result.version;
  });

  // Fetch current organization and display in topbar if resolved
  if (user) {
    api.getCurrentOrganization().then(org => {
      if (!org?.description) return;
      const topbar = document.getElementById('topbar');
      if (!topbar) return;
      const titleEl = topbar.querySelector('.topbar-title');
      if (titleEl) {
        titleEl.textContent = `VMSMan.IO for ${org.description}`;
      }
    }).catch(() => { /* ignore - org display is optional */ });
  }
}

/**
 * Update topbar with connection info (called after getting connection details)
 */
export function updateConnectionInfo(connectionType: string, remoteIp: string): void {
  const topbar = document.getElementById('topbar');
  if (!topbar) return;

  // Find or create connection info element
  let connInfo = document.getElementById('topbar-connection');
  if (!connInfo) {
    connInfo = document.createElement('span');
    connInfo.id = 'topbar-connection';
    connInfo.className = 'topbar-connection';
    // Insert before the user info
    const userInfo = topbar.querySelector('.topbar-user');
    if (userInfo) {
      topbar.insertBefore(connInfo, userInfo);
      // Add separator
      const sep = document.createElement('span');
      sep.className = 'topbar-separator';
      sep.textContent = '|';
      topbar.insertBefore(sep, userInfo);
    }
  }

  connInfo.innerHTML = `Connected via <strong>${escapeHtml(connectionType)}</strong> (${escapeHtml(remoteIp)})`;
}
